import Avatar from '@/components/base/Avatar';
import Button from '@/components/base/Button';
import StatusBadge from '@/components/base/StatusBadge';
import { formatMoney } from '@/utils/format';
import { payoutExceptions } from '@/utils/payout';
import type { Payout } from '@/types/portal';

interface PayoutExceptionsPanelProps {
  rows: Payout[];
  canManage: boolean;
  onRetry: (payout: Payout) => void;
}

export default function PayoutExceptionsPanel({
  rows,
  canManage,
  onRetry,
}: PayoutExceptionsPanelProps) {
  const exceptions = payoutExceptions(rows);

  return (
    <section className="rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center justify-between gap-2 border-b border-background-200 px-5 py-4">
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950">
            Exceptions
          </h3>
          <p className="mt-0.5 text-xs text-foreground-500">
            Disbursements needing a decision or a retry
          </p>
        </div>
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-base text-primary-700">
          <i className="ri-error-warning-line" />
        </span>
      </header>

      {exceptions.length === 0 ? (
        <p className="px-5 py-6 text-sm text-foreground-500">
          No exceptions. Every disbursement is on track.
        </p>
      ) : (
        <ul className="divide-y divide-background-100">
          {exceptions.map((row) => (
            <li key={row.id} className="px-5 py-3.5">
              <div className="flex items-center gap-2.5">
                <Avatar src={row.avatar} alt={row.creatorName} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground-950">
                    {row.creatorName}
                  </p>
                  <p className="truncate text-xs text-foreground-500">
                    {row.reference} · {formatMoney(row.amount, row.currency)}
                  </p>
                </div>
                <StatusBadge status={row.status} />
              </div>
              <p className="mt-2 text-xs text-foreground-600">{row.exception}</p>
              {row.status === 'Failed' ? (
                <div className="mt-2">
                  <Button
                    size="sm"
                    variant="outline"
                    icon="ri-refresh-line"
                    disabled={!canManage}
                    onClick={() => onRetry(row)}
                  >
                    Retry payout
                  </Button>
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}